import React from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { ArrowDown, CalendarDays, ChevronRight, Clock, MapPin } from 'lucide-react';
import type { InvitationCustomData, WeddingCustomData, WeddingEventItem, WeddingMediaItem } from '@/src/types';
import { CoupleStorySection } from '@/src/modules/sections/wedding/CoupleStorySection';
import { FamilyTreeSection } from '@/src/modules/sections/wedding/FamilyTreeSection';
import { MediaShowcaseSection } from '@/src/modules/sections/wedding/MediaShowcaseSection';
import { PreWeddingStorySection } from '@/src/modules/sections/wedding/PreWeddingStorySection';
import { WeddingTimelineSection } from '@/src/modules/sections/wedding/WeddingTimelineSection';
import { Reveal } from '@/src/modules/sections/wedding/shared';
import { Watermark } from '../../components/Watermark';
import { extractCoupleNames, parseFlexibleDate } from '@/src/infrastructure/lib/weddingDataHelper';

type WeddingExperienceData = {
  eventName: string;
  eventDate: string;
  eventVenue: string;
  hostName: string;
  customMessage?: string;
  rsvpLink?: string;
  mediaUrls?: string[];
  customData?: InvitationCustomData | null;
  isPremium?: boolean;
};

type Props = {
  data: WeddingExperienceData;
  hasPartnerAttribution?: boolean;
};

function splitCoupleNames(eventName: string, data?: WeddingExperienceData) {
  const groom = data?.customData?.wedding?.groom;
  const bride = data?.customData?.wedding?.bride;
  const groomName = groom?.fullName || groom?.nickname;
  const brideName = bride?.fullName || bride?.nickname;
  if (groomName && brideName) {
    return { first: groomName, second: brideName, display: `${groomName} & ${brideName}` };
  }

  const extracted = extractCoupleNames(eventName);
  if (extracted?.groom && extracted?.bride) {
    return { first: extracted.groom, second: extracted.bride, display: `${extracted.groom} & ${extracted.bride}` };
  }

  const cleaned = (eventName || '').trim();
  const parts = cleaned
    .split(/\s+(?:and|weds|wed|with|आणि|व|संग|सोबत)\s+|[&+|]/i)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    const second = parts.slice(1).join(' & ');
    return { first: parts[0], second, display: `${parts[0]} & ${second}` };
  }

  if (cleaned) {
    return { first: cleaned, second: '', display: cleaned };
  }

  return { first: 'Groom', second: 'Bride', display: 'Wedding Celebration' };
}

function formatDate(iso: string) {
  const date = parseFlexibleDate(iso);
  if (!date) return { day: 'Save', month: 'The Date', full: iso || 'Date to be announced', time: 'Together' };

  try {
    return {
      day: new Intl.DateTimeFormat('en-IN', { day: '2-digit' }).format(date),
      month: new Intl.DateTimeFormat('en-IN', { month: 'long' }).format(date),
      full: new Intl.DateTimeFormat('en-IN', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(date),
      time: new Intl.DateTimeFormat('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }).format(date),
    };
  } catch {
    return { day: 'Save', month: 'The Date', full: iso, time: 'Together' };
  }
}

function scrollToRsvp() {
  document.getElementById('rsvp')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function toImageItems(urls: string[]): WeddingMediaItem[] {
  return urls.filter(Boolean).map((url, index) => ({
    type: 'image',
    url,
    caption: index === 0 ? 'A day to remember' : undefined,
  }));
}

function googleMapsUrl(locationName: string) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationName)}`;
}

function fallbackEvents(data: WeddingExperienceData): WeddingEventItem[] {
  return [
    {
      title: 'Wedding',
      date: data.eventDate,
      venue: data.eventVenue,
      description: data.customMessage || 'Join us for the ceremony, blessings, and celebration.',
    },
  ];
}

function hasText(value?: string) {
  return Boolean(value?.trim());
}

function hasPersonDetails(person: WeddingCustomData['groom']) {
  return Boolean(person && [
    person.fullName,
    person.nickname,
    person.profession,
    person.fatherName,
    person.motherName,
    person.homeAddress,
    person.about,
    person.photoUrl,
  ].some(hasText));
}

function hasFamilyData(groups?: WeddingCustomData['familyGroups']) {
  return Boolean(groups?.some((group) =>
    hasText(group.groupTitle) &&
    group.members.some((member) => hasText(member.name) && hasText(member.relation)),
  ));
}

function hasMediaData(items?: WeddingMediaItem[]) {
  return Boolean(items?.some((item) => hasText(item.url)));
}

/**
 * Dark scrim on hero photo — sits above theme gradients so the effect is
 * visible. A deep, even semi-transparent overlay clearly dims the photograph
 * (it stays visible — never opaque) so the event-name text reads with strong,
 * premium contrast. No blur.
 */
const HERO_IMAGE_SCRIM =
  'linear-gradient(180deg, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0.64) 50%, rgba(0,0,0,0.70) 100%)';

function resolveWeddingData(data: WeddingExperienceData): WeddingCustomData {
  const custom = data.customData?.wedding ?? {};

  return {
    groom: custom.groom,
    bride: custom.bride,
    familyGroups: custom.familyGroups,
    weddingEvents: custom.weddingEvents?.length ? custom.weddingEvents : fallbackEvents(data),
    preWeddingStory: custom.preWeddingStory,
    mediaShowcase: custom.mediaShowcase,
  };
}

export function CinematicWeddingExperience({ data, hasPartnerAttribution }: Props) {
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.22], [0, 120]);
  const heroScale = useTransform(scrollYProgress, [0, 0.22], [1, 1.08]);
  const couple = splitCoupleNames(data.eventName, data);
  const date = formatDate(data.eventDate);
  const [heroFromData, ...galleryFromData] = data.mediaUrls ?? [];
  const heroImage = data.customData?.wedding?.mediaShowcase?.find((item) => item.type === 'image' && item.url)?.url || heroFromData;
  const mediaFromUrls = toImageItems(galleryFromData);
  const wedding = resolveWeddingData(data);
  const storyMessage = data.customMessage ?? '';
  const coupleStoryVisible = hasPersonDetails(wedding.groom) || hasPersonDetails(wedding.bride) || hasText(storyMessage);
  const familyGroups = hasFamilyData(wedding.familyGroups)
    ? wedding.familyGroups!.map((group) => ({
        ...group,
        members: group.members.filter((member) => hasText(member.name) && hasText(member.relation)),
      }))
    : [];
  const preWeddingMoments = wedding.preWeddingStory?.moments?.filter((item) => hasText(item.url)) ?? [];
  const preWeddingVisible = hasText(wedding.preWeddingStory?.title) || hasText(wedding.preWeddingStory?.description) || preWeddingMoments.length > 0;
  const mediaShowcase = hasMediaData(wedding.mediaShowcase) ? wedding.mediaShowcase!.filter((item) => hasText(item.url)) : mediaFromUrls;
  const venueMapUrl = googleMapsUrl(data.eventVenue);
  const heroLines = couple.second ? [couple.first, '&', couple.second] : [couple.first];

  return (
    <main className="min-h-screen overflow-hidden bg-[#11130f] text-[#fffaf0] selection:bg-[#d7b56d] selection:text-[#11130f]">
      <section className="relative isolate flex min-h-[92svh] items-end px-5 pb-10 sm:min-h-screen sm:px-8 lg:px-14 lg:pb-14">
        {heroImage ? (
          <motion.div className="absolute inset-0 -z-30" style={{ y: heroY, scale: heroScale }}>
            <img src={heroImage} alt="" className="h-full w-full object-cover" />
          </motion.div>
        ) : (
          <motion.div
            className="absolute inset-0 -z-30 bg-[radial-gradient(circle_at_72%_18%,rgba(215,181,109,0.34),transparent_28%),linear-gradient(135deg,#31211a,#11130f_58%,#1c1510)]"
            style={{ y: heroY, scale: heroScale }}
          />
        )}
        <div className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_72%_18%,rgba(232,190,111,0.28),transparent_28%),linear-gradient(90deg,rgba(8,10,8,0.9),rgba(8,10,8,0.38)_58%,rgba(8,10,8,0.72)),linear-gradient(0deg,rgba(17,19,15,0.96),transparent_48%)]" />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-[15]"
          style={{ background: HERO_IMAGE_SCRIM }}
        />
        <motion.div
          aria-hidden="true"
          animate={{ y: [0, -14, 0], rotate: [0, 2, 0] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute right-[-16vw] top-[11vh] -z-10 h-[48vw] w-[48vw] rounded-full border border-[#f0d894]/20"
        />

        <div className="relative z-10 grid w-full items-end gap-10 lg:grid-cols-[1.12fr_0.88fr]">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              className="mb-4 text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-[#f1dca0] sm:text-xs"
            >
              Together with their families
            </motion.p>
            <motion.h1
              initial="hidden"
              animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.13 } } }}
              className="max-w-5xl text-[clamp(2.65rem,9vw,7.8rem)] font-semibold leading-[0.9] tracking-normal [overflow-wrap:anywhere] sm:leading-[0.86]"
            >
              {heroLines.map((line) => (
                <motion.span
                  key={line}
                  variants={{
                    hidden: { opacity: 0, y: 70, filter: 'blur(10px)' },
                    show: { opacity: 1, y: 0, filter: 'blur(0px)' },
                  }}
                  transition={{ duration: 0.92, ease: [0.16, 1, 0.3, 1] }}
                  className="block break-words"
                >
                  {line}
                </motion.span>
              ))}
            </motion.h1>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.82, delay: 0.4, ease: 'easeOut' }}
            className="justify-self-start border-l border-[#f1dca0]/28 pl-5 lg:justify-self-end"
          >
            <p className="text-xs uppercase tracking-[0.22em] text-[#f7eee0]/70">Wedding celebration</p>
            <p className="mt-4 text-4xl font-semibold text-[#f1dca0] sm:text-5xl">{date.day}</p>
            <p className="text-base text-[#f7eee0]/82 sm:text-lg">{date.month}</p>
            <button
              type="button"
              onClick={scrollToRsvp}
              className="mt-7 inline-flex items-center gap-3 rounded-full bg-[#f7eee0] px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-[#17140e] transition hover:-translate-y-1 hover:bg-[#f1dca0] sm:px-6"
            >
              RSVP <ChevronRight size={16} />
            </button>
          </motion.div>
        </div>

        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 items-center gap-2 text-[0.68rem] uppercase tracking-[0.24em] text-[#f7eee0]/62 sm:flex"
        >
          Scroll <ArrowDown size={15} />
        </motion.div>
      </section>

      <section className="relative bg-[#f8f1e5] px-5 py-20 text-[#1f1b15] sm:px-8 lg:px-14 lg:py-28">
        <div className="absolute inset-y-0 right-0 w-1/3 bg-[#d8a1a0]/18" />
        <div className="relative grid items-center gap-12 lg:grid-cols-[0.82fr_1.18fr]">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.26em] text-[#a15f54]">Welcome</p>
            <h2 className="mt-4 text-[clamp(2.35rem,5.8vw,6rem)] font-semibold leading-[0.98] tracking-normal">
              A wedding told in light, movement, and memory.
            </h2>
          </Reveal>
          <Reveal delay={0.12} className="space-y-7">
            {storyMessage.trim() && (
              <p className="max-w-3xl text-lg leading-8 text-[#40372d] sm:text-2xl sm:leading-10">{storyMessage}</p>
            )}
            <div className="grid gap-5 sm:grid-cols-3">
              <div className="min-w-0 border-t border-[#1f1b15]/22 pt-5">
                <CalendarDays className="mb-4 text-[#a15f54]" />
                <p className="text-xs uppercase tracking-[0.18em] text-[#75685b]">Date</p>
                <p className="mt-2 text-lg font-semibold leading-snug">{date.full}</p>
              </div>
              <div className="min-w-0 border-t border-[#1f1b15]/22 pt-5">
                <Clock className="mb-4 text-[#a15f54]" />
                <p className="text-xs uppercase tracking-[0.18em] text-[#75685b]">Time</p>
                <p className="mt-2 text-lg font-semibold">{date.time}</p>
              </div>
              <div className="min-w-0 border-t border-[#1f1b15]/22 pt-5">
                <MapPin className="mb-4 text-[#a15f54]" />
                <p className="text-xs uppercase tracking-[0.18em] text-[#75685b]">Venue</p>
                <a
                  href={venueMapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 block max-w-full break-words text-lg font-semibold leading-snug underline decoration-[#a15f54]/35 underline-offset-4 transition hover:text-[#8f514a] hover:decoration-[#a15f54] focus:outline-none focus:ring-2 focus:ring-[#a15f54]/40"
                >
                  {data.eventVenue}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {coupleStoryVisible && (
        <CoupleStorySection groom={wedding.groom ?? {}} bride={wedding.bride ?? {}} story={storyMessage} />
      )}
      <WeddingTimelineSection events={wedding.weddingEvents ?? []} />
      <FamilyTreeSection groups={familyGroups} />
      {preWeddingVisible && (
        <PreWeddingStorySection
          title={wedding.preWeddingStory?.title ?? ''}
          description={wedding.preWeddingStory?.description ?? ''}
          moments={preWeddingMoments}
        />
      )}
      <MediaShowcaseSection items={mediaShowcase} />

      <section className="grid min-h-[82svh] bg-[#f8f1e5] text-[#1f1b15] lg:grid-cols-[1.06fr_0.94fr]">
        <Reveal className="flex flex-col justify-center px-5 py-20 sm:px-8 lg:px-14">
          <p className="text-xs font-semibold uppercase tracking-[0.26em] text-[#a15f54]">Venue</p>
          <h2 className="mt-4 text-[clamp(2.35rem,5.8vw,6rem)] font-semibold leading-[0.98] tracking-normal">
            Meet us where the story becomes a celebration.
          </h2>
          <div className="mt-10 border-t border-[#1f1b15]/22 pt-7">
            <MapPin className="mb-5 text-[#a15f54]" size={30} />
            <a
              href={venueMapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block max-w-2xl break-words text-2xl font-semibold leading-tight underline decoration-[#a15f54]/35 underline-offset-4 transition hover:text-[#8f514a] hover:decoration-[#a15f54] focus:outline-none focus:ring-2 focus:ring-[#a15f54]/40 sm:text-3xl"
            >
              {data.eventVenue}
            </a>
            <p className="mt-5 text-base leading-8 text-[#51463a] sm:text-lg">{date.full} at {date.time}</p>
          </div>
        </Reveal>
        <div className="relative min-h-[58vh] overflow-hidden bg-[#211712]">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(215,181,109,0.28),transparent_38%),radial-gradient(circle_at_70%_30%,rgba(248,241,229,0.24),transparent_25%)]" />
          <div className="absolute inset-6 border border-[#f8f1e5]/20 sm:inset-8" />
          <motion.div
            animate={{ x: ['-5%', '5%', '-5%'], y: ['4%', '-4%', '4%'] }}
            transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute left-1/2 top-1/2 h-60 w-60 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#d7b56d]/24 blur-3xl sm:h-72 sm:w-72"
          />
          <div className="absolute inset-0 flex items-center justify-center p-10 text-center">
            <a
              href={venueMapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="max-w-xl break-words text-3xl font-semibold leading-tight text-[#f8f1e5] underline decoration-[#d7b56d]/35 underline-offset-4 transition hover:text-[#d7b56d] focus:outline-none focus:ring-2 focus:ring-[#d7b56d]/50 sm:text-4xl"
            >
              {data.eventVenue}
            </a>
          </div>
        </div>
      </section>

      <section id="rsvp" className="relative min-h-[86svh] bg-[#17140e] px-5 py-20 text-[#fffaf0] sm:px-8 lg:px-14 lg:py-28">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(161,95,84,0.34),transparent_26%),radial-gradient(circle_at_88%_76%,rgba(215,181,109,0.26),transparent_30%)]" />
        <Reveal className="relative flex min-h-[62vh] flex-col items-start justify-center">
          <p className="text-xs font-semibold uppercase tracking-[0.26em] text-[#d7b56d]">RSVP</p>
          <h2 className="mt-4 max-w-5xl text-[clamp(3rem,8vw,8rem)] font-semibold leading-[0.9] tracking-normal">
            Come celebrate with us.
          </h2>
          <p className="mt-7 max-w-2xl text-lg leading-8 text-[#f7eee0]/72 sm:text-xl sm:leading-9">
            Your presence is the gift we will remember. Join {couple.display} for a day designed around love, family, and unforgettable memories.
          </p>
          {data.rsvpLink ? (
            <a
              href={data.rsvpLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-9 inline-flex items-center gap-3 rounded-full bg-[#f7eee0] px-7 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-[#17140e] transition hover:-translate-y-1 hover:bg-[#d7b56d]"
            >
              Confirm attendance <ChevronRight size={18} />
            </a>
          ) : (
            <div className="mt-9 border-t border-[#f7eee0]/18 pt-6 text-base text-[#f7eee0]/70">
              RSVP details will be shared by the host.
            </div>
          )}
        </Reveal>
      </section>
      <Watermark isPremium={data.isPremium} hasPartnerAttribution={hasPartnerAttribution} />
    </main>
  );
}
